// Phase 22C — TA/DA Approval grid (manager + admin)
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, ChevronDown, ChevronRight, Check, X, MapPin, Wallet, Bike, Package, Download,
  ShieldCheck, ShieldAlert, Ban, Paperclip, ArrowRight, Building2,
} from "lucide-react";
import { api, API_BASE, getToken, formatApiError, photoRefPath } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const BADGE = {
  submitted:        "bg-amber-100 text-amber-800 border-amber-200",
  manager_approved: "bg-sky-100 text-sky-800 border-sky-200",
  admin_approved:   "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected:         "bg-rose-100 text-rose-800 border-rose-200",
  draft:            "bg-neutral-100 text-neutral-700 border-neutral-200",
};

export default function TadaApproval() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [viewerStage, setViewerStage] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 100 };
      if (statusFilter) params.status = statusFilter;
      const r = await api.get("/tada/approval", { params });
      setRows(r.data.items || []);
      setViewerStage(r.data.viewer_stage);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const doExport = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set("status", statusFilter);
      const url = `${API_BASE}/tada/approval/export${qs.toString() ? "?" + qs.toString() : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error?.message || j.detail?.message || j.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const stamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 13);
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `tada_approval_grid_${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
      toast.success("Grid exported to Excel");
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setExporting(false); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="tada-approval-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Money" }, { label: "TA/DA Approval" }]}
        title="TA/DA Approval"
        subtitle="Manager and admin review of travel and DA claims, with the monthly limit used so far."
        favorite={{ key: "admin.tada-approval", type: "screen", label: "TA/DA Approval", route: "/admin/tada-approval" }}
        testid="tadaapproval-page-header"
      />

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">TA/DA Approval</h1>
          <p className="text-xs text-neutral-500 mt-0.5">
            Viewing as <b>{viewerStage || "…"}</b> — you'll see claims awaiting your action + your team's history.
          </p>
        </div>
        <Button onClick={doExport} disabled={exporting}
                className="tulsi-gradient text-white"
                data-testid="tada-approval-download">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
          {exporting ? "Preparing…" : "Download Excel"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="tada-approval-chips">
        {[
          {v:"", label:"All"},
          {v:"submitted", label:"Pending Manager"},
          {v:"manager_approved", label:"Pending Admin"},
          {v:"admin_approved", label:"Approved"},
          {v:"rejected", label:"Rejected"},
        ].map(o => (
          <button key={o.v}
            onClick={() => setStatusFilter(o.v)}
            className={"px-3 py-1 rounded-full text-xs font-semibold border " +
              (statusFilter === o.v
                ? "bg-[#6D2C91] text-white border-[#6D2C91]"
                : "bg-white text-neutral-700 border-neutral-200 hover:border-[#6D2C91]/50")}
            data-testid={`tada-approval-chip-${o.v || "all"}`}>
            {o.label}
          </button>
        ))}
      </div>

      {loading && <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></div>}
      {!loading && rows.length === 0 && (
        <Card className="border-neutral-200 rounded-2xl"><CardContent className="py-16 text-center text-neutral-400 text-sm">No claims match this filter.</CardContent></Card>
      )}

      <div className="space-y-2">
        {rows.map(r => (
          <ApprovalRow key={r.id} claim={r}
                        expanded={expanded === r.id}
                        onToggle={() => setExpanded(e => e === r.id ? null : r.id)}
                        onChanged={load} />
        ))}
      </div>
    </div>
  );
}

function PolicyContextCard({ ctx }) {
  const p = ctx.policy;
  const cap = ctx.monthly_cap_inr || 0;
  const consumed = ctx.monthly_consumed_inr || 0;
  const projected = ctx.monthly_projected_inr || 0;
  const overCap = !!ctx.over_cap;
  const capPct = cap > 0 ? Math.min(100, Math.round((projected / cap) * 100)) : 0;
  return (
    <div
      data-testid="policy-context-card"
      className={`rounded-xl border p-3 text-xs space-y-2 ${
        overCap ? "border-red-300 bg-red-50" : "border-[#6D2C91]/20 bg-white"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className={`h-4 w-4 ${overCap ? "text-red-600" : "text-[#6D2C91]"}`} />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-400">Policy</div>
            <div className="font-semibold text-sm text-neutral-900">
              <span className="font-mono text-[11px] mr-1">{p.code}</span>{p.name}
            </div>
          </div>
        </div>
        {cap > 0 ? (
          <Badge
            data-testid="policy-monthly-cap-badge"
            className={overCap ? "bg-red-600 text-white" : "bg-emerald-100 text-emerald-800"}
          >
            {overCap ? <><ShieldAlert className="h-3 w-3 mr-1" />Over cap</> : "Within cap"}
          </Badge>
        ) : (
          <Badge variant="outline">No monthly cap</Badge>
        )}
      </div>

      {cap > 0 && (
        <div>
          <div className="flex justify-between text-[11px] text-neutral-600 mb-1">
            <span>Month spend</span>
            <span data-testid="policy-monthly-values">
              ₹{projected.toLocaleString("en-IN")} <span className="text-neutral-400">/ ₹{cap.toLocaleString("en-IN")}</span>
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-neutral-200 overflow-hidden">
            <div
              className={overCap ? "h-full bg-red-600" : "h-full bg-[#6D2C91]"}
              style={{ width: `${capPct}%` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
            <span>Consumed (other claims): ₹{consumed.toLocaleString("en-IN")}</span>
            <span>This claim: ₹{(ctx.this_claim_total_inr || 0).toLocaleString("en-IN")}</span>
          </div>
        </div>
      )}
    </div>
  );
}


function ApprovalRow({ claim, expanded, onToggle, onChanged }) {
  const [full, setFull] = useState(null);
  const [policyCtx, setPolicyCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    setLoading(true);
    Promise.all([
      api.get(`/tada/claims/${claim.id}/full`),
      api.get(`/tada/claims/${claim.id}/policy-context`).catch(() => ({ data: null })),
    ])
      .then(([r1, r2]) => { setFull(r1.data); setPolicyCtx(r2.data); })
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [expanded, claim.id]);

  const bulkApprove = async () => {
    setBusy(true);
    try {
      await api.post(`/tada/claims/${claim.id}/approve-all`, {});
      toast.success("Approved");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const bulkReject = async () => {
    if (!reason.trim() || reason.trim().length < 3) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      await api.post(`/tada/claims/${claim.id}/reject-all`, { reason });
      toast.success("Rejected");
      setRejectOpen(false); setReason(""); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-neutral-200 rounded-2xl overflow-hidden" data-testid={`tada-approval-row-${claim.id}`}>
      <button className="w-full text-left" onClick={onToggle} data-testid={`tada-approval-toggle-${claim.id}`}>
        <div className="p-3 flex items-center gap-3">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-neutral-900">
              {claim.user_name || "—"} <span className="text-neutral-400 font-normal">· {claim.user_role || claim.user_role_snapshot}</span>
            </div>
            <div className="text-[11px] text-neutral-500">
              {claim.claim_date} · {claim.market_type_code}
              {claim.gps_km_snapshot != null && <span className="ml-2 text-[#6D2C91]"><MapPin className="h-3 w-3 inline" /> GPS {claim.gps_km_snapshot} km</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-neutral-500">Claimed</div>
            <div className="text-lg font-bold">₹ {(claim.total_claimed_inr || 0).toLocaleString("en-IN")}</div>
          </div>
          <Badge className={"border text-[10px] " + (BADGE[claim.status] || BADGE.draft)}>
            {claim.status.replace("_"," ")}
          </Badge>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-neutral-100 p-3 bg-neutral-50 space-y-2" data-testid={`tada-approval-detail-${claim.id}`}>
          {loading && <Loader2 className="h-4 w-4 animate-spin mx-auto text-neutral-400" />}
          {full && (
            <>
              {policyCtx?.policy && (
                <PolicyContextCard ctx={policyCtx} />
              )}
              <div className="grid grid-cols-1 gap-2">
                {full.items.map(it => {
                  const chk = (policyCtx?.item_checks || []).find(c => c.item_id === it.id);
                  return (
                    <ItemRow key={it.id} item={it}
                              policyCheck={chk}
                              gpsKm={full.gps_km_snapshot}
                              canAct={["submitted", "manager_approved"].includes(claim.status)}
                              claimId={claim.id}
                              onChanged={() => { onChanged(); /* refresh detail */
                                api.get(`/tada/claims/${claim.id}/full`).then(r => setFull(r.data));
                                api.get(`/tada/claims/${claim.id}/policy-context`).then(r => setPolicyCtx(r.data)).catch(()=>{}); }} />
                  );
                })}
              </div>
              {["submitted", "manager_approved"].includes(claim.status) && (
                <div className="flex gap-2 pt-2 border-t border-neutral-200">
                  <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700"
                          onClick={bulkApprove} disabled={busy}
                          data-testid={`tada-approve-all-${claim.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve all
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setRejectOpen(true)}
                          data-testid={`tada-reject-all-${claim.id}`}>
                    <X className="h-3.5 w-3.5 mr-1 text-red-600" /> Reject all
                  </Button>
                </div>
              )}
              {claim.stage_history && claim.stage_history.length > 0 && (
                <div className="pt-2 border-t border-neutral-200">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">History</div>
                  {claim.stage_history.slice(-5).map((h, i) => (
                    <div key={i} className="text-[11px] text-neutral-600">
                      <span className="font-mono text-neutral-400">{new Date(h.ts).toLocaleString([], {dateStyle:"short", timeStyle:"short"})}</span>
                      {" · "}{h.actor_role} <b>{h.action}</b>
                      {h.extra?.delta ? <span className="ml-1 text-amber-700">Δ ₹{h.extra.delta}</span> : null}
                      {h.comment ? <span className="ml-1 italic">"{h.comment}"</span> : null}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <Dialog open={rejectOpen} onOpenChange={v => !v && setRejectOpen(false)}>
        <DialogContent data-testid={`tada-reject-dialog-${claim.id}`}>
          <DialogHeader>
            <DialogTitle>Reject claim</DialogTitle>
            <DialogDescription>The user will be able to edit and resubmit. Reason will be shown to them.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} placeholder="Reason (required)" value={reason}
                    onChange={e => setReason(e.target.value)}
                    data-testid="tada-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700"
                    onClick={bulkReject} disabled={busy}
                    data-testid="tada-reject-confirm">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Attachment view — /api/photos/{ref} needs a bearer token a plain <a href>
// can't send, so fetch the bytes ourselves and open the resulting blob URL
// in a new tab (same pattern as RetailerDetailsSheet.jsx's storefront photo).
async function openAttachment(ref) {
  try {
    const res = await fetch(`${API_BASE}/photos/${photoRefPath(ref)}`,
      { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (e) { toast.error("Could not open attachment"); }
}

function ItemRow({ item, gpsKm, canAct, claimId, onChanged, policyCheck }) {
  const claimed = item.item_type === "travel" ? item.computed_amount_inr
                    : item.item_type === "other" ? item.expense_amount_inr
                    : item.da_amount_inr;
  const [approved, setApproved] = useState(item.approved_amount_inr ?? claimed);
  const [comment, setComment] = useState(item.item_comment || "");
  const [busy, setBusy] = useState(false);

  const delta = (parseFloat(approved) || 0) - (parseFloat(claimed) || 0);
  const isDa = item.item_type === "da";
  const done = item.per_item_status && item.per_item_status !== "pending";

  const doApprove = async () => {
    setBusy(true);
    try {
      await api.post(`/tada/claims/${claimId}/items/${item.id}/approve`, {
        approved_amount_inr: parseFloat(approved),
        comment: comment || null,
      });
      toast.success("Item approved");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const doReject = async () => {
    if (!comment.trim() || comment.trim().length < 3) { toast.error("Comment/reason required (min 3 chars)"); return; }
    setBusy(true);
    try {
      await api.post(`/tada/claims/${claimId}/items/${item.id}/reject`, { reason: comment });
      toast.success("Item rejected");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-neutral-200 rounded-xl" data-testid={`tada-approval-item-${item.id}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Badge className={
            item.item_type === "da" ? "bg-[#6D2C91]/10 text-[#6D2C91] border-[#6D2C91]/20" :
            item.item_type === "travel" ? "bg-sky-100 text-sky-800 border-sky-200" :
            "bg-amber-100 text-amber-800 border-amber-200"
          }>
            {item.item_type === "da" && <><Wallet className="h-3 w-3 inline mr-1" /> DA · {item.market_type_code}</>}
            {item.item_type === "travel" && <><Bike className="h-3 w-3 inline mr-1" /> {item.mode_code}</>}
            {item.item_type === "other" && <><Package className="h-3 w-3 inline mr-1" /> {item.claim_type_code}</>}
          </Badge>
          {item.item_type === "travel" && (
            <>
              <Badge className="bg-neutral-100 text-neutral-700 text-[10px]" data-testid={`tada-user-km-${item.id}`}>
                User KM: {item.km_entered}
              </Badge>
              {gpsKm != null && (
                <Badge className={"text-[10px] border " + (
                  Math.abs((gpsKm - item.km_entered) / (item.km_entered || 1)) > 0.2
                    ? "bg-amber-50 text-amber-800 border-amber-200"
                    : "bg-emerald-50 text-emerald-800 border-emerald-200"
                )} data-testid={`tada-gps-km-${item.id}`}>
                  GPS KM: {gpsKm.toFixed(1)}
                </Badge>
              )}
            </>
          )}
          {policyCheck && policyCheck.ok === false && (
            <Badge
              data-testid={`policy-check-${item.id}`}
              className="bg-red-100 text-red-800 border-red-200 text-[10px] flex items-center gap-1"
            >
              {policyCheck.item_type === "travel" && !policyCheck.allowed ? (
                <><Ban className="h-3 w-3" /> Mode blocked</>
              ) : (
                <><ShieldAlert className="h-3 w-3" /> Over limit ₹{policyCheck.max_amount_inr}</>
              )}
            </Badge>
          )}
          {policyCheck && policyCheck.ok === true && policyCheck.max_amount_inr != null && (
            <Badge
              data-testid={`policy-check-${item.id}`}
              className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px] flex items-center gap-1"
            >
              <ShieldCheck className="h-3 w-3" /> Within ₹{policyCheck.max_amount_inr}
            </Badge>
          )}
          {done && (
            <Badge className={"ml-auto " + (item.per_item_status === "approved"
              ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800")}>
              {item.per_item_status}
            </Badge>
          )}
        </div>

        {item.item_type === "travel" && (item.from_place || item.to_place) && (
          <div className="flex items-center gap-1.5 text-xs text-neutral-600"
               data-testid={`tada-route-${item.id}`}>
            <MapPin className="h-3 w-3 text-neutral-400" />
            {item.from_place || "—"} <ArrowRight className="h-3 w-3 text-neutral-400" /> {item.to_place || "—"}
          </div>
        )}
        {item.claim_type_code === "NIGHT_STAY" && item.city && (
          <div className="flex items-center gap-1.5 text-xs text-neutral-600"
               data-testid={`tada-city-${item.id}`}>
            <Building2 className="h-3 w-3 text-neutral-400" /> {item.city}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <div>
            <div className="text-[10px] text-neutral-500 uppercase">Claim ₹</div>
            <div className="font-semibold" data-testid={`tada-claim-amt-${item.id}`}>₹ {claimed}</div>
          </div>
          <div>
            <div className="text-[10px] text-neutral-500 uppercase">Approve ₹</div>
            <Input type="number" min="0" step="0.01" value={approved}
                   onChange={e => setApproved(e.target.value)}
                   disabled={done || !canAct}
                   className={"h-8 text-sm " + (Math.abs(delta) > 0.01 ? "border-amber-400 bg-amber-50" : "")}
                   data-testid={`tada-approve-amt-${item.id}`} />
            {Math.abs(delta) > 0.01 && (
              <div className="text-[10px] text-amber-700 mt-0.5" data-testid={`tada-delta-${item.id}`}>
                Δ {delta > 0 ? "+" : ""}{delta.toFixed(2)}
              </div>
            )}
          </div>
        </div>

        {item.remark && (
          <div className="text-[11px] text-neutral-500 italic">"{item.remark}"</div>
        )}
        {item.item_type === "other" && item.image_ref && (
          <button type="button" onClick={() => openAttachment(item.image_ref)}
                  className="inline-flex items-center gap-1 text-[11px] text-[#6D2C91] font-semibold"
                  data-testid={`tada-attachment-${item.id}`}>
            <Paperclip className="h-3 w-3" /> View attachment
          </button>
        )}

        {!done && canAct && (
          <div className="flex items-center gap-2 pt-1">
            {!isDa && (
              <Input placeholder="Comment (required for reject)" value={comment}
                     onChange={e => setComment(e.target.value)}
                     className="h-8 text-sm flex-1" />
            )}
            <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700"
                    onClick={doApprove} disabled={busy}
                    data-testid={`tada-approve-item-${item.id}`}>
              <Check className="h-3 w-3" />
            </Button>
            {!isDa && (
              <Button size="sm" variant="outline" onClick={doReject} disabled={busy}
                      data-testid={`tada-reject-item-${item.id}`}>
                <X className="h-3 w-3 text-red-600" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
